/**
 * Change Detection Service
 * Handles lightweight polling to check for flight data changes
 */

import { authFetch } from '../utils/authFetch';
import { API_BASE_URL } from '../config/environment';

class ChangeDetectionService {
  constructor() {
    this.lastChecked = localStorage.getItem('lastChecked') || new Date().toISOString();
  }

  /**
   * Check for changes since the last check
   * @param {string} userId - User ID to check changes for
   * @returns {Promise<Array>} Array of changed trips with tripId and lastModified
   */
  async checkForChanges(userId) {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/trips/changes?userId=${userId}&since=${this.lastChecked}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.changes && data.changes.length > 0) {
        // Update lastChecked to current time to avoid re-detecting same changes
        this.lastChecked = new Date().toISOString();
        localStorage.setItem('lastChecked', this.lastChecked);
        return data.changes; // Array of {tripId, lastModified}
      }
      
      return [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Mark changes as viewed (update lastChecked timestamp)
   */
  markAsViewed() {
    this.lastChecked = new Date().toISOString();
    localStorage.setItem('lastChecked', this.lastChecked);
  }

  /**
   * Reset the last checked timestamp (useful for testing or when user wants to see all changes)
   */
  resetLastChecked() {
    this.lastChecked = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(); // 24 hours ago
    localStorage.setItem('lastChecked', this.lastChecked);
  }

  /**
   * Get the current lastChecked timestamp
   */
  getLastChecked() {
    return this.lastChecked;
  }
}

// Export a singleton instance
export default new ChangeDetectionService();
